import footprint, { FootprintComponentKind } from "@onefootprint/footprint-js";
import {
  useModalContext,
  WSButton,
  WSElementProps,
  WSMessageBox
} from "@wingspanhq/fe-component-library";
import React from "react";
import { queryCache } from "react-query";
import { useHistory } from "react-router-dom";
import { useWSMutation } from "../../../query/helpers";
import { useUserId } from "../../../query/hooks/helpers";
import { useFeatureFlags } from "../../../query/hooks/useFeatureFlags";
import {
  QUERY_VERIFICATIONS,
  useQueryVerifications
} from "../../../query/onboarding/queries/useQueryVerifications";
import { QUERY_USERS_ACTIVITIES } from "../../../query/users/keys";
import { getVerificationMissingData } from "../../../services/api/onboarding/missingFields";
import { usersService } from "../../../services/users";
import { openIntercom } from "../../../utils/intercom";

type Props = WSElementProps;

export const PaymentsStatusBanner: React.FC<Props> = ({ ...elementProps }) => {
  const userId = useUserId();
  const history = useHistory();
  const { openModal } = useModalContext();
  const queryFeatureFlags = useFeatureFlags();
  const queryVerificaitons = useQueryVerifications();

  const [startSetupOver] = useWSMutation(
    async () => {
      if (queryFeatureFlags.data?.cip) {
        history.push("/member/cip");
      } else {
        history.replace("/member/invoices/set-up");
      }

      await usersService.activity.update(userId, {
        flows: {
          paymentsSetup: {
            complete: false,
            version: 3,
            currentStep: 0,
            totalSteps: 5
          }
        }
      });
    },
    {
      dependencies: [QUERY_USERS_ACTIVITIES]
    }
  );

  const handleBankingUpdate = async () => {
    try {
      const missingData = await getVerificationMissingData("Banking");

      if (missingData.completeOnboardingToken) {
        const component = footprint.init({
          kind: FootprintComponentKind.Verify,
          variant: "modal",
          authToken: missingData.completeOnboardingToken,
          onComplete: () => {
            queryCache.invalidateQueries(QUERY_VERIFICATIONS);
          },
          options: {
            showLogo: false
          }
        });
        component.render();
      }
    } catch (error) {
      console.error(error);
      openIntercom();
    }
  };

  const handleTaxUpdate = async () => {
    try {
      const missingData = await getVerificationMissingData("Tax");

      if (missingData.completeOnboardingToken) {
        const component = footprint.init({
          kind: FootprintComponentKind.Verify,
          variant: "modal",
          authToken: missingData.completeOnboardingToken,
          onComplete: () => {
            queryCache.invalidateQueries(QUERY_VERIFICATIONS);
          },
          options: {
            showLogo: false
          }
        });
        component.render();
      }
    } catch (error) {
      console.error(error);
      openIntercom();
    }
  };

  if (!queryVerificaitons.data) {
    return null;
  }

  const verifications = queryVerificaitons.data;

  return verifications.tax === "None" ? (
    <WSMessageBox.Error
      size="Large"
      icon="alert-circle"
      noBorder
      {...elementProps}
    >
      To use Wingspan payments we'll need to collect some information,{" "}
      <WSButton.Link onClick={startSetupOver}>
        click here to get started
      </WSButton.Link>
      .
    </WSMessageBox.Error>
  ) : verifications.tax === "Pending" ? (
    <WSMessageBox.Warning icon="time" noBorder {...elementProps}>
      <b>Digital payments pending.</b> It can take up to 48 hours to enable
      digital payments.{" "}
      <WSButton.Link onClick={openIntercom}>Learn more</WSButton.Link>
    </WSMessageBox.Warning>
  ) : verifications.tax === "UpdateRequired" ? (
    <WSMessageBox.Error icon="time" noBorder {...elementProps}>
      We were not able to verify your tax identification information,{" "}
      <WSButton.Link onClick={handleTaxUpdate}>click here</WSButton.Link> to
      verify and update your information.
    </WSMessageBox.Error>
  ) : verifications.banking === "UpdateRequired" ? (
    <WSMessageBox.Regular noBorder {...elementProps}>
      <b>You're all set to receive payments!</b> We need some additional
      information to enable all Wingspan features.{" "}
      <WSButton.Link onClick={handleBankingUpdate}>
        Click here to upload required documents
      </WSButton.Link>
      .
    </WSMessageBox.Regular>
  ) : null;
};
